import { useQuery, keepPreviousData, type UseQueryOptions } from "@tanstack/react-query";
import { useEffect } from "react";
import { AxiosError } from "axios";

import { axiosGetData } from "@shared/api/clients";
import type {
  PaginatedResult,
  PaginatedQueryParams,
} from "@shared/api/types/pagination.types";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

interface UsePaginatedQueryOptions<TData, TFilters = Record<string, never>> {
  endpoint: string;
  queryKeyBase: string[];
  params?: PaginatedQueryParams<TFilters>;
  enabled?: boolean;
  onSuccess?: (data: PaginatedResult<TData>) => void;
  onError?: (error: AxiosError) => void;
  queryOptions?: Omit<
    UseQueryOptions<PaginatedResult<TData>, AxiosError>,
    "queryKey" | "queryFn" | "placeholderData" | "enabled"
  >;
}

export const usePaginatedQuery = <TData, TFilters = Record<string, never>>({
  endpoint,
  queryKeyBase,
  params,
  enabled = true,
  onSuccess,
  onError,
  queryOptions,
}: UsePaginatedQueryOptions<TData, TFilters>) => {
  const cleanParams = Object.fromEntries(
    Object.entries({
      page: DEFAULT_PAGE,
      limit: DEFAULT_LIMIT,
      ...params,
    }).filter(([, value]) => value !== undefined),
  );

  const query = useQuery<PaginatedResult<TData>, AxiosError>({
    queryKey: [...queryKeyBase, cleanParams],
    queryFn: () => axiosGetData<PaginatedResult<TData>>(endpoint, cleanParams),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
    retry: false,
    enabled,
    ...queryOptions,
  });

  useEffect(() => {
    if (query.isSuccess && query.data) {
      onSuccess?.(query.data);
    }
  }, [query.isSuccess, query.data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (query.isError && query.error) {
      onError?.(query.error);
    }
  }, [query.isError, query.error]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    ...query,
    isEmpty: query.isSuccess && query.data?.data.length === 0,
    isLoadingFirstTime: query.isLoading && !query.isPlaceholderData,
    isRefreshing: query.isFetching && query.isPlaceholderData,
    totalPages: query.data?.meta?.totalPages ?? 0,
    totalItems: query.data?.meta?.totalItems ?? 0,
  };
};